import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X } from "lucide-react";
import { api } from "../../lib/api";

const empty = { question: "", answer: "", keywords: "", order: 0 };

export default function FaqManager() {
  const [faqs, setFaqs] = useState([]);
  const [editing, setEditing] = useState(null); // null | "new" | id
  const [form, setForm] = useState(empty);

  const load = () => api.get("/faqs").then((r) => setFaqs(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm({ ...empty, order: faqs.length }); setEditing("new"); };
  const openEdit = (f) => { setForm({ question: f.question, answer: f.answer, keywords: f.keywords || "", order: f.order || 0 }); setEditing(f.id); };
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async (e) => {
    e.preventDefault();
    if (!form.question.trim() || !form.answer.trim()) { toast.error("Question and answer are required."); return; }
    const payload = { ...form, order: Number(form.order) || 0 };
    try {
      if (editing === "new") await api.post("/admin/faqs", payload);
      else await api.put(`/admin/faqs/${editing}`, payload);
      toast.success("FAQ saved");
      setEditing(null);
      load();
    } catch {
      toast.error("Save failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this FAQ?")) return;
    await api.delete(`/admin/faqs/${id}`);
    toast.success("Deleted");
    load();
  };

  return (
    <div data-testid="faq-manager">
      <div className="mb-5 flex items-center justify-between gap-4">
        <p className="text-sm text-muted-foreground">
          These answers power the support chat bubble. If a visitor's question doesn't match any FAQ, they're referred to your Contact page.
        </p>
        <button onClick={openNew} data-testid="add-faq-btn" className="flex shrink-0 items-center gap-2 rounded-full bg-crimson px-5 py-2.5 text-sm font-bold text-white hover:bg-crimson-hover">
          <Plus className="h-4 w-4" /> Add FAQ
        </button>
      </div>

      <div className="space-y-3">
        {faqs.length === 0 && <div className="rounded-2xl border border-line bg-white p-8 text-center text-muted-foreground">No FAQs yet. Add your first one.</div>}
        {faqs.map((f) => (
          <div key={f.id} className="rounded-2xl border border-line bg-white p-5" data-testid={`faq-item-${f.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="font-bold text-ink">{f.question}</div>
                <p className="mt-1 text-sm text-muted-foreground">{f.answer}</p>
                {f.keywords && <div className="mt-2 text-xs text-muted-foreground">Match terms: <span className="font-mono">{f.keywords}</span></div>}
              </div>
              <div className="flex shrink-0 gap-2">
                <button onClick={() => openEdit(f)} data-testid={`edit-faq-${f.id}`} className="rounded-lg border border-line p-2 hover:border-ink"><Pencil className="h-4 w-4" /></button>
                <button onClick={() => remove(f.id)} data-testid={`delete-faq-${f.id}`} className="rounded-lg border border-line p-2 text-crimson hover:border-crimson"><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="faq-modal">
          <form onSubmit={save} className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-3xl bg-white p-8">
            <div className="flex items-center justify-between">
              <h2 className="font-display text-xl font-bold text-ink">{editing === "new" ? "Add FAQ" : "Edit FAQ"}</h2>
              <button type="button" onClick={() => setEditing(null)} data-testid="faq-modal-close"><X className="h-5 w-5" /></button>
            </div>
            <div className="mt-5 space-y-4">
              <div>
                <label className="text-sm font-bold text-ink">Question</label>
                <input value={form.question} onChange={set("question")} data-testid="faq-f-question" placeholder="e.g. Do you offer free shipping?" className="mt-1 w-full rounded-xl border border-line px-3 py-2 outline-none focus:border-crimson" />
              </div>
              <div>
                <label className="text-sm font-bold text-ink">Answer</label>
                <textarea rows={4} value={form.answer} onChange={set("answer")} data-testid="faq-f-answer" placeholder="The exact answer customers will see." className="mt-1 w-full resize-none rounded-xl border border-line px-3 py-2 outline-none focus:border-crimson" />
              </div>
              <div>
                <label className="text-sm font-bold text-ink">Match terms <span className="font-normal text-muted-foreground">(optional)</span></label>
                <input value={form.keywords} onChange={set("keywords")} data-testid="faq-f-keywords" placeholder="extra words shoppers might use, e.g. delivery ups postage" className="mt-1 w-full rounded-xl border border-line px-3 py-2 outline-none focus:border-crimson" />
                <p className="mt-1 text-xs text-muted-foreground">Helps the chat match different phrasings. Separate words with spaces.</p>
              </div>
              <div>
                <label className="text-sm font-bold text-ink">Display order</label>
                <input type="number" value={form.order} onChange={set("order")} data-testid="faq-f-order" className="mt-1 w-32 rounded-xl border border-line px-3 py-2 outline-none focus:border-crimson" />
              </div>
            </div>
            <button type="submit" data-testid="faq-modal-save" className="mt-6 w-full rounded-full bg-crimson py-3 font-bold text-white hover:bg-crimson-hover">Save</button>
          </form>
        </div>
      )}
    </div>
  );
}
